import React, { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import OtpInput from 'react-otp-input'
import { toast } from 'react-toastify'
import axios from 'axios'
import { QRCodeSVG } from 'qrcode.react'
import { storeTokenInLocalStorage } from '../../../../_helper/userHooks/common'

interface LoginData {
  email: string
  password: string
  providerCode?: string
  token: string
  isActivated?: boolean
}

export function LoginMFA() {
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [showQrCode, setShowQrCode] = useState(true)
  const [showOtpInput, setShowOtpInput] = useState(false)
  const history = useHistory()
  const location = useLocation()

  // Get login data from location state
  const loginData = (location.state as { loginData?: LoginData })?.loginData || {
    token: '',
    isActivated: false
  }
  const { token, isActivated } = loginData

  useEffect(() => {
    // If no login data is present, redirect to login
    if (!token) {
      history.push('/auth/login')
      return
    }
    
    // If user has already activated MFA, show code input directly
    if (isActivated) {
      setShowQrCode(false)
      setShowOtpInput(true)
    } else {
      // Fetch QR code for authenticator app
      fetchQrCode(token)
    }
  }, [token, isActivated, history])

  const fetchQrCode = async (token) => {
    try {
      setLoading(true)
      const response = await axios.post('/users/enable-mfa',{}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      
      if (response.data?.data) {
        setQrCodeUrl(response.data.data)
        setShowQrCode(true)
        setShowOtpInput(false)
      } else {
        setError('Failed to generate QR code. Please try again.')
        toast.error('Failed to generate QR code. Please try again.')
      }
    } catch (error) {
      console.error('QR code fetch error:', error)
      setError(error.response?.data?.message || 'Failed to generate QR code. Please try again.')
      toast.error(error.response?.data?.message || 'Failed to generate QR code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleOtpChange = async (otpValue: string) => {
    setOtp(otpValue)
    
    // Auto-submit when 6 digits are entered
    if (otpValue.length === 6) {
      await verifyOtp(otpValue)
    }
  }

  const verifyOtp = async (otpValue: string) => {
    try {
      setLoading(true)
      setError('')

      const response = await axios.post("/users/verify-mfa",{userCode:otpValue}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      if (response.data?.status) {
        toast.success('MFA has been successfully set up and enabled as preferred')
        await storeTokenInLocalStorage(token);
        // Redirect to dashboard after successful MFA verification
        history.push('/dashboard')
        window.location.reload()
      } else {
        setError('Invalid OTP code. Please try again.')
        toast.error('Invalid OTP code. Please try again.')
      }
    } catch (error) {
      console.error('MFA verification error:', error)
      setError(error.response?.data?.message || 'MFA verification failed. Please try again.')
      toast.error(error.response?.data?.message || 'MFA verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleEnterCode = () => {
    setShowQrCode(false)
    setShowOtpInput(true)
  }

  const handleBackToQr = () => {
    setShowQrCode(true)
    setShowOtpInput(false)
    setOtp('')
  }

  return (
    <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'> 
    <div className='form w-100'>
      {/* begin::Heading */}
      <div className='text-center mb-10'>
        <h1 className='text-dark mb-3'>Multi-Factor Authentication</h1>
        <div className='text-gray-400 fw-bold fs-6'>
          {showQrCode ? 'Scan the QR code with your authenticator app' : 'Enter the 6-digit code from your authenticator app'}
        </div>
        {isActivated && (
          <div className='text-info fw-bold fs-6 mt-2'>
            Please enter the 6-digit code from your authenticator app
          </div>
        )}
      </div>

      {loading && (
        <div className='fv-row mb-10'>
          <div className='d-flex justify-content-center'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className='fv-row mb-10'>
          <div className='alert alert-danger'>
            <div className='alert-text'>{error}</div>
          </div>
        </div>
      )}

      {showQrCode && !loading && (
        <div className='fv-row mb-10'>
          <div className='d-flex justify-content-center'>
            <div className='text-center'>
              {qrCodeUrl && (
                <div className='mb-10'>
                  <QRCodeSVG size={200} value={qrCodeUrl} />
                </div>
              )}
              <button
                type='button'
                className='btn btn-lg btn-primary'
                onClick={handleEnterCode}
              >
                Enter Code
              </button>
            </div>
          </div>
        </div>
      )}

      {showOtpInput && !loading && (
        <div className='fv-row mb-10'>
          <div className='d-flex justify-content-center'>
            <OtpInput
              value={otp}
              onChange={handleOtpChange}
              numInputs={6}
              renderInput={(props) => <input {...props} />}
              inputStyle={{
                width: '3rem',
                height: '3rem',
                margin: '0 0.5rem',
                fontSize: '1rem',
                borderRadius: 4,
                border: '1px solid rgba(0,0,0,0.3)',
                textAlign: 'center'
              }}
              shouldAutoFocus={true}
            />
          </div>
        </div>
      )}

      {showOtpInput && !loading && (
        <div className='text-center'>
          <button
            type='button'
            className='btn btn-lg btn-primary w-100 mb-5'
            onClick={() => verifyOtp(otp)}
            disabled={otp.length !== 6 || loading}
          >
            {!loading && <span className='indicator-label'>Verify Code</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Verifying...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      )}

      {showOtpInput && !loading && (
        <div className='text-center'>
          <button
            type='button'
            className='btn btn-link text-muted'
            onClick={handleBackToQr}
          >
            ← Back to QR Code
          </button>
        </div>
      )}
    </div>
    </div>
  )
} 